import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Minus, Plus, Loader2 } from 'lucide-react';
import Header from '../components/layout/Header';
import AnnouncementBar from '../components/layout/AnnouncementBar';
import Footer from '../components/layout/Footer';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { getSessionId } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Social media links for floating icons
const socialMediaLinks = [
  {
    name: "TikTok",
    url: "https://www.tiktok.com/@itsyungdexy04",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    ),
    color: "#000000",
  },
  {
    name: "Instagram",
    url: "https://www.instagram.com/itsyungdexy/",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    color: "#E4405F",
  },
  {
    name: "YouTube",
    url: "https://www.youtube.com/@itsyungdexy",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    color: "#FF0000",
  },
];

// Floating Social Icon Component
const FloatingSocialIcon = ({ social, index }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const startX = Math.random() * 100;
    const startY = 20 + Math.random() * 60;
    setPosition({ x: startX, y: startY });
    
    const animate = () => {
      setPosition(prev => ({
        x: prev.x + (Math.sin(Date.now() / 2000 + index) * 0.3),
        y: prev.y + (Math.cos(Date.now() / 3000 + index) * 0.2),
      }));
    };
    
    const interval = setInterval(animate, 50);
    return () => clearInterval(interval);
  }, [index]);

  return (
    <a
      href={social.url}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed z-40 p-3 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 hover:scale-125 transition-all duration-300 shadow-lg"
      style={{
        left: `${Math.min(Math.max(position.x, 5), 90)}%`,
        top: `${Math.min(Math.max(position.y, 15), 85)}%`,
        color: social.color,
      }}
      title={social.name}
    >
      {social.icon}
    </a>
  );
};

const CartPage = () => {
  const { 
    getCartItemsWithDetails, 
    updateQuantity, 
    removeItem, 
    subtotal, 
    cartCount,
    loading 
  } = useCart();
  const { formatPrice } = useCurrency();
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const cartItems = getCartItemsWithDetails();
  const shipping = subtotal >= 50 ? 0 : 3;
  const total = subtotal + shipping;

  const handleCheckout = async () => {
    try {
      setCheckoutLoading(true);
      const sessionId = getSessionId();
      
      const response = await fetch(`${API_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin_url: window.location.origin,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to initiate checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] via-[#2d1f3d] to-[#1a1a1a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white/60">Loading cart...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] via-[#2d1f3d] to-[#1a1a1a] relative">
      <AnnouncementBar />
      <Header />
      
      {/* Floating Social Media Icons */}
      {socialMediaLinks.map((social, index) => (
        <FloatingSocialIcon key={social.name} social={social} index={index} />
      ))}
      
      <main className="py-12 relative z-10">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="cart-title">YOUR CART</h1>
          <p className="text-white/60 mb-8" data-testid="cart-count">{cartCount} {cartCount === 1 ? 'item' : 'items'}</p>

          {cartItems.length === 0 ? (
            <div className="text-center py-16 bg-white/10 backdrop-blur-sm rounded-lg" data-testid="empty-cart">
              <div className="text-6xl mb-4">🛒</div>
              <p className="text-white/60 mb-6">Your cart is currently empty.</p>
              <Link
                to="/shop"
                className="inline-block bg-white text-black px-8 py-3 text-sm font-medium tracking-wider hover:bg-gray-200 transition-colors rounded"
                data-testid="start-shopping-btn"
              >
                START SHOPPING
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg divide-y divide-white/10">
                  {cartItems.map((item, index) => (
                    <div key={`${item.product_id}-${item.size}`} className="p-6 flex gap-6" data-testid={`cart-item-${index}`}>
                      <Link to={`/product/${item.product_id}`} className="shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-24 h-24 object-cover rounded"
                        />
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/product/${item.product_id}`}
                          className="font-medium text-white hover:text-white/80 block truncate"
                        >
                          {item.name}
                        </Link>
                        <p className="text-sm text-white/50 mt-1">
                          {item.color} / {item.size}
                        </p>
                        <p className="text-sm font-bold text-white mt-2">{formatPrice(item.price)}</p>
                        
                        <div className="flex items-center justify-between mt-4">
                          <div className="flex items-center border border-white/20 rounded">
                            <button
                              onClick={() => updateQuantity(item.product_id, item.size, item.quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                              data-testid={`decrease-qty-${index}`}
                            >
                              <Minus size={14} />
                            </button>
                            <span className="w-10 text-center text-sm font-medium text-white" data-testid={`qty-${index}`}>{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.product_id, item.size, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                              data-testid={`increase-qty-${index}`}
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                          <button
                            onClick={() => removeItem(item.product_id, item.size)}
                            className="text-white/40 hover:text-red-400 transition-colors p-2"
                            data-testid={`remove-item-${index}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 sticky top-32">
                  <h2 className="text-lg font-bold text-white mb-4">Order Summary</h2>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/60">Subtotal</span>
                      <span className="font-medium text-white" data-testid="subtotal">{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Shipping</span>
                      <span className="font-medium text-white" data-testid="shipping">
                        {shipping === 0 ? (
                          <span className="text-green-400">FREE</span>
                        ) : (
                          formatPrice(shipping)
                        )}
                      </span>
                    </div>
                    {subtotal < 50 && subtotal > 0 && (
                      <p className="text-xs text-white/50 bg-white/5 p-2 rounded">
                        Add {formatPrice(50 - subtotal)} more for free shipping!
                      </p>
                    )}
                  </div>
                  
                  <div className="flex justify-between text-lg font-bold border-t border-white/20 pt-4 mt-4">
                    <span className="text-white">Total</span>
                    <span className="text-white" data-testid="total">{formatPrice(total)}</span>
                  </div>
                  
                  <button 
                    onClick={handleCheckout}
                    disabled={checkoutLoading || cartItems.length === 0}
                    className="w-full mt-6 bg-white text-black py-4 text-sm font-medium tracking-wider hover:bg-gray-200 transition-colors rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="checkout-btn"
                  >
                    {checkoutLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        PROCESSING...
                      </>
                    ) : (
                      'CHECKOUT'
                    )}
                  </button>
                  
                  <Link 
                    to="/shop"
                    className="block text-center mt-4 text-sm text-white/60 hover:text-white underline"
                  >
                    Continue Shopping
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CartPage;
